import { useCallback, useMemo, useState, type RefObject } from 'react';
import type { ChatMessage as ChatMessageType, Citation, DraftInfo, MessageAttachedDocument } from '@/types/chat';
import type { FileStatus } from '@/types/file';
import { MessageRole } from '@/constants';
import { ChatMessage } from '@/ui/features/chat/components/ChatMessage';
import { SavePromptToToolkitModal } from './SavePromptToToolkitModal';
import { ProcessingSteps } from './ProcessingSteps';

export type ChatMessageListProps = {
  messages: ChatMessageType[];
  searchQuery: string;
  messageRefs: RefObject<Map<string, HTMLDivElement | null>>;
  onCitationClick: (citation: Citation) => void;
  globalOrderedUuids: string[];
  chatCitations: Record<string, Citation>;
  documentsByMessageId?: Map<string, MessageAttachedDocument[]>;
  promptAttachmentStatusById?: Map<string, FileStatus>;
  isWaitingForDocumentProcessing?: boolean;
  /** True while a response is streaming — gates the processing steps above that response. */
  isStreamingResponse?: boolean;
  onAttachedDocumentClick?: (documentId: string) => void;
  canEditChat: boolean;
  onDraftClick?: (draft: DraftInfo) => void;
  onEditPrompt?: (prompt: string) => void;
  onExpandChatInput?: () => void;
  documentNameById?: Map<string, string>;
};

export function ChatMessageList({
  messages,
  searchQuery,
  messageRefs,
  onCitationClick,
  globalOrderedUuids,
  chatCitations,
  documentsByMessageId,
  promptAttachmentStatusById,
  isWaitingForDocumentProcessing,
  isStreamingResponse,
  onAttachedDocumentClick,
  canEditChat,
  onDraftClick,
  onEditPrompt,
  onExpandChatInput,
  documentNameById,
}: ChatMessageListProps) {
  const [savePromptInitialValue, setSavePromptInitialValue] = useState<string | null>(null);

  const openSavePromptToToolkit = useCallback((prompt: string) => {
    setSavePromptInitialValue(prompt);
  }, []);

  const closeSavePromptToToolkit = useCallback(() => {
    setSavePromptInitialValue(null);
  }, []);

  const lastAssistantMessageId = useMemo(
    () => [...messages].reverse().find((msg) => msg.role === MessageRole.Assistant)?.id,
    [messages],
  );

  return (
    <div className="relative pt-2">
      {messages.map((msg) => (
        <div
          className="relative"
          key={msg.id}
          ref={(el) => {
            messageRefs.current.set(msg.id, el);
          }}
        >
          {/* Above the response it belongs to, not below it — otherwise each new step pushes
              the text the reader is following further down the screen. */}
          {isStreamingResponse && msg.id === lastAssistantMessageId && msg.processingSteps?.length ? (
            <ProcessingSteps steps={msg.processingSteps} />
          ) : null}

          <ChatMessage
            message={msg}
            onCitationClick={onCitationClick}
            searchQuery={searchQuery}
            globalOrderedUuids={globalOrderedUuids}
            chatCitations={chatCitations}
            documentsByMessageId={documentsByMessageId}
            promptAttachmentStatusById={promptAttachmentStatusById}
            isWaitingForDocumentProcessing={isWaitingForDocumentProcessing}
            onAttachedDocumentClick={onAttachedDocumentClick}
            canEditChat={canEditChat}
            onDraftClick={onDraftClick}
            onSavePromptToToolkit={openSavePromptToToolkit}
            onEditPrompt={onEditPrompt}
            onExpandChatInput={onExpandChatInput}
            isLastAssistantMessage={msg.id === lastAssistantMessageId}
            documentNameById={documentNameById}
          />
        </div>
      ))}
      <SavePromptToToolkitModal
        visible={savePromptInitialValue !== null}
        onHide={closeSavePromptToToolkit}
        initialPrompt={savePromptInitialValue ?? ''}
      />
    </div>
  );
}
